<template>
  <div
    class="filter-popup"
    :class="{ 'pb-0': filterIsTextInput }"
    @mousemove="mouseMoveHandle"
    @mousedown="focusElementHandler"
    @mouseup="blurElementHandler"
    @click="focusInput"
  >
    <div
      class="filter-search-input"
      :class="{ 'border-b-0': !showSearchInput || filterIsTextInput }"
    >
      <input
        ref="searchInput"
        v-model="searchValue"
        v-autofocus
        :class="{ 'hidden-input': !showSearchInput }"
        class="popup-window-text-field flex-grow"
        :placeholder="placeholder"
        @keyup="checkKey"
        @keydown="checkDownKey"
        @input="sendSearchQuery"
      />

      <ShortcutKeyPill
        v-if="showKeyPill"
        class="absolute top-3 right-4"
        :text="shortcut.shortcutKey"
        :background-color="shortcut.background"
        :font-color="shortcut.text"
        :border-color="shortcut.border"
      />
      <div
        v-if="filterIsTextInput"
        class="flex pr-3 items-center gap-x-1"
      >
        <ActionButtons
          @confirm="selectText"
          @cancel="clearText"
        />
      </div>
    </div>
    <vue-scroll
      v-if="searchResults.length > 0"
      ref="searchResultsScroll"
      class="filter-selection"
      :ops="{
        scrollPanel: { scrollingX: false },
        bar: { keepShow: true, size: '4px' },
        rail: { gutterOfSide: '2px' },
      }"
    >
      <div
        v-if="title"
        class="popup-title uppercase text-text-default"
      >
        {{ title }}
      </div>
      <div class="filters-list">
        <FilterDropdownItem
          v-for="(item, index) in searchResults"
          :key="`${index}-${item.key || ''}-${item.id}`"
          :index="index"
          :selected-item-index="selectedIndex"
          :item="item"
          :items="searchResults"
          :selected-item-check="selectedItemCheck"
          :disabled="item.disabled"
          :hover-preview-disabled="hoverPreviewDisabled"
          @item-click="itemClick(item, index)"
          @item-right-side-click="onItemRightSideClick(item, index)"
          @item-hover="setSelectedIndex(index)"
          @scroll-to-item="scrollToItem"
        />
      </div>
      <div
        v-if="dropdownFooter"
        class="dropdown-footer"
        @click.stop
      >
        <p
          class="text-text-placeholder underline cursor-pointer"
          @click="footerClickHandler"
        >
          {{ footerText }}
        </p>
      </div>
    </vue-scroll>
    <div
      v-if="searchResults.length === 0 && !filterIsTextInput"
      class="search-results text-text-default p-3 text-sm"
    >
      {{ emptyStateSearchText }}
    </div>
  </div>
</template>

<script>
import FilterDropdownItem from '@/components/widgets/filters/FilterDropdownItem';
import inputDropdown from '@/mixins/inputDropdown';
import elementFocusMixin from '@/mixins/elementFocusMixin';
import scrollToElementInVuescroll from '@/utilities/scrollToElementInVuescroll';
import ShortcutKeyPill from '@/components/functional/ShortcutKeyPill';
import ActionButtons from '@/components/widgets/ActionButtons';

export default {
  name: 'FilterDropdown',

  components: {
    FilterDropdownItem,
    ShortcutKeyPill,
    ActionButtons,
  },

  mixins: [inputDropdown, elementFocusMixin],

  props: {
    placeholder: {
      type: String,
      required: false,
      default: '',
    },
    hideFilterPopup: {
      type: Function,
      required: true,
    },
    showShortcutKeyPill: {
      type: Boolean,
      required: false,
      default: false,
    },
    shortcut: {
      type: [Object, Function],
      required: false,
      default: () => ({
        background: '',
        text: '',
        border: '',
        shortcutKey: 'F',
      }),
    },
    searchResults: {
      type: Array,
      required: true,
    },
    selectedItemCheck: {
      type: Number,
      required: false,
      default: -1,
    },
    onItemClick: {
      type: Function,
      required: true,
    },
    onItemRightSideClick: {
      type: Function,
      required: true,
    },
    selectOnlySearchResult: {
      type: Function,
      required: false,
      default: () => {},
    },
    showSearchInput: {
      type: Boolean,
      required: false,
      default: true,
    },
    shouldUseQuery: {
      type: Boolean,
      required: false,
      default: false,
    },
    selectedFilterType: {
      type: String,
      required: false,
      default: '',
    },
    filters: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    scrollToSelected: {
      type: Boolean,
      required: false,
      default: false,
    },
    scrollToSelectedSpeed: {
      type: Number,
      required: false,
      default: undefined,
    },
    hoverPreviewDisabled: {
      type: Boolean,
      required: false,
      default: true,
    },
    title: {
      type: String,
      required: false,
      default: '',
    },
    dropdownFooter: {
      type: Boolean,
      required: false,
      default: false,
    },
    footerText: {
      type: String,
      required: false,
      default: '',
    },
  },

  emits: ['esc', 'query-change'],

  data() {
    return {
      focusTimeout: null,
      searchValue: '',
    };
  },

  computed: {
    emptyStateSearchText() {
      return this.searchValue !== ''
        ? this.translate('noResultsFound')
        : this.translate('searchForResults');
    },
    filterIsTextInput() {
      return ['title'].includes(this.selectedFilterType);
    },
    showKeyPill() {
      return this.showShortcutKeyPill && this.showSearchInput && !this.filterIsTextInput;
    },
  },

  watch: {
    searchValue(value) {
      this.$emit('query-change', value);
    },
    selectedFilterType(value) {
      if (value === 'title') {
        this.searchValue = this.filters?.include?.title || '';
        this.$nextTick(() => {
          // set the cursor to the end of the input
          this.$refs.searchInput.focus();
          this.$refs.searchInput.setSelectionRange(
            this.searchValue.length,
            this.searchValue.length
          );
        });
      } else {
        this.searchValue = '';
      }
    },
  },

  mounted() {
    this.$nextTick(() => {
      if (!this.isMobile) {
        this.focusInput();
      }

      // Scroll to selected item if prop is enabled and we have a selected item
      if (this.scrollToSelected) {
        this.scrollToSelectedItem();
      }
    });
  },

  unmounted() {
    clearTimeout(this.focusTimeout);
    this.focusTimeout = null;
    if (this.shouldUseQuery) {
      this.$store.dispatch('setSearchResults', {
        count: 0,
        cursor: '',
        cards: [],
      });
    }
  },

  methods: {
    scrollToItem(event) {
      scrollToElementInVuescroll(event, this.$refs.searchResultsScroll, this.scrollToSelectedSpeed);
    },

    scrollToSelectedItem() {
      if (this.selectedItemCheck !== -1 && this.searchResults.length > 0) {
        this.setSelectedIndex(this.selectedItemCheck);
      }
    },

    focusInput() {
      this.focusTimeout = setTimeout(() => {
        this.$refs.searchInput?.focus();
      }, 100);
    },
    selectSearchResult(item, index) {
      if (this.filterIsTextInput) {
        this.selectText();
        return;
      }

      this.itemClick(item, index);
    },
    selectText() {
      this.itemClick({
        value: this.searchValue,
        key: this.selectedFilterType,
      });
    },
    clearText() {
      this.clearSearchValue();
      this.onItemClick({
        value: '',
        key: this.selectedFilterType,
      });
    },
    itemClick(item, index) {
      this.onItemClick?.(item, index);
      this.focusInput();
    },
    handleEsc(e) {
      this.$emit('esc', e);
    },
    setSelectedIndex(index = 0) {
      this.selectedIndex = index;
    },
    clearSearchValue() {
      this.searchValue = '';
    },

    sendSearchQuery($event) {
      if (this.shouldUseQuery && !['Enter', 'Escape'].includes($event.key)) {
        this.sendQuery();
        this.scrollToTop();
      }
    },

    sendQuery(qry = this.searchValue) {
      return this.$store.dispatch('sendSearchQuery', {
        query: qry,
        advanceOptions: {
          archived: true,
          types: ['cards'],
          fields: 'title',
          parentCard: true,
        },
      });
    },

    footerClickHandler(event) {
      this.$emit('footerClick', event);
    },
  },
};
</script>

<style lang="scss">
@import '@/assets/scss/filterPopup.scss';

.filter-search-input {
  .hidden-input {
    line-height: 0 !important;
    font-size: 0 !important;
    height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    border: none !important;
    position: absolute !important;
    outline: none !important;
  }
}
</style>
